import {
	createContext,
	type PropsWithChildren,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";

export type ThemeMode = "light" | "dark" | "system";
type ResolvedTheme = "light" | "dark";

type ThemeContextValue = {
	theme: ThemeMode;
	resolvedTheme: ResolvedTheme;
	setTheme: (theme: ThemeMode) => void;
};

const STORAGE_KEY = "forgedock.theme.mode";
const DARK_MODE_MEDIA_QUERY = "(prefers-color-scheme: dark)";

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isThemeMode(value: string): value is ThemeMode {
	return value === "light" || value === "dark" || value === "system";
}

function getSystemTheme(): ResolvedTheme {
	if (typeof window === "undefined") {
		return "light";
	}

	return window.matchMedia(DARK_MODE_MEDIA_QUERY).matches ? "dark" : "light";
}

function getInitialTheme(): ThemeMode {
	if (typeof window === "undefined") {
		return "system";
	}

	const storedValue = window.localStorage.getItem(STORAGE_KEY);
	if (!storedValue || !isThemeMode(storedValue)) {
		return "system";
	}

	return storedValue;
}

function applyThemeToDocument(resolvedTheme: ResolvedTheme) {
	if (typeof document === "undefined") {
		return;
	}

	const root = document.documentElement;
	root.classList.toggle("dark", resolvedTheme === "dark");
	root.style.colorScheme = resolvedTheme;
}

export function ThemeProvider({ children }: PropsWithChildren) {
	const [theme, setTheme] = useState<ThemeMode>(() => getInitialTheme());
	const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => getSystemTheme());

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		const mediaQuery = window.matchMedia(DARK_MODE_MEDIA_QUERY);
		const onChange = () => setSystemTheme(mediaQuery.matches ? "dark" : "light");

		onChange();
		mediaQuery.addEventListener("change", onChange);
		return () => mediaQuery.removeEventListener("change", onChange);
	}, []);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		window.localStorage.setItem(STORAGE_KEY, theme);
	}, [theme]);

	const resolvedTheme = theme === "system" ? systemTheme : theme;

	useEffect(() => {
		applyThemeToDocument(resolvedTheme);
	}, [resolvedTheme]);

	const value = useMemo<ThemeContextValue>(
		() => ({
			theme,
			resolvedTheme,
			setTheme,
		}),
		[theme, resolvedTheme],
	);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error("useTheme must be used inside ThemeProvider");
	}
	return context;
}
